// Shown when a signed-in user has no agency yet.
// - canCreateAgency: first-run onboarding form
// - otherwise: invite-only waiting room (ask admin for ?invite= link)
import { useState } from "react";
import { useAuth } from "@/state/auth";
import { api } from "@/lib/api";

export function CreateAgency() {
  const { user, createAgency, signOut, canCreateAgency, signupMode } = useAuth();
  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(detectedTz);
  const [mrrGoal, setMrrGoal] = useState<string>("");
  const [seedChecklist, setSeedChecklist] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setErr(null);
    try {
      const ws = await createAgency(name.trim());
      // Everything below is best-effort; failures don't block the user
      // getting into their new agency — they can set these in Settings.
      const patches: Record<string, string> = {};
      if (timezone.trim()) patches.timezone = timezone.trim();
      if (Object.keys(patches).length) {
        try { await api.put("/api/settings", patches); } catch { /* ignore */ }
      }
      const goalNum = Math.round(Number(mrrGoal) * 100);
      if (Number.isFinite(goalNum) && goalNum > 0) {
        try { await api.put("/api/agency/settings", { mrr_goal_cents: String(goalNum) }); } catch { /* ignore */ }
      }
      if (seedChecklist) {
        try {
          const tpl = await api.post<{ id: number }>("/api/checklist-templates", {
            name: "New customer onboarding",
            kind: "onboarding",
            description: "Starter template — edit or replace to fit how you deliver.",
          });
          const items = [
            "Kickoff call scheduled",
            "Access + logins collected",
            "First deliverable shipped",
            "Follow-up scheduled for week 2",
          ];
          for (const t of items) {
            try { await api.post(`/api/checklist-templates/${tpl.id}/items`, { title: t }); } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }
      void ws;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not create agency");
    } finally { setBusy(false); }
  }

  if (!canCreateAgency) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-8">
        <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 shadow-lg text-center">
          <div className="font-display text-2xl font-semibold tracking-tight text-foreground">
            Invite only
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{user?.email}</span>.
            This Flowio deployment does not allow creating a new agency
            {signupMode === "open" ? "" : " without an allowlisted email"}.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Ask an agency admin to invite you (Settings → Team → Send invite link).
            Open the link while signed in with this Google account and you&apos;ll join automatically.
          </p>
          <button
            type="button"
            onClick={() => signOut()}
            className="mt-6 w-full rounded-md border border-border py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 shadow-lg">
        <div className="text-center">
          <div className="font-display text-2xl font-semibold tracking-tight text-foreground">
            Welcome to Flowio
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Signed in as {user?.email}. A few quick answers and you&apos;re in — everything is editable later.
          </p>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Agency name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Agency"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Timezone</span>
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="America/New_York"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Auto-detected from your browser. Controls the &quot;today&quot; boundary for reports.
            </span>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">MRR goal ($, optional)</span>
            <input
              type="number"
              value={mrrGoal}
              onChange={(e) => setMrrGoal(e.target.value)}
              placeholder="30000"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={seedChecklist} onChange={(e) => setSeedChecklist(e.target.checked)} />
            Seed a starter onboarding checklist template
          </label>
          {err && <p className="text-sm text-destructive" role="alert">{err}</p>}
          <button
            type="submit"
            disabled={!name.trim() || busy}
            className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? "Setting up…" : "Create agency"}
          </button>
        </form>
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-4 w-full text-xs text-muted-foreground hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
